import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, FileText, Loader2, Pencil, Save, X } from 'lucide-react';
import { Drawer } from './ui/Drawer';
import { Pill } from './ui/Pill';
import { AiOutput } from './ui/AiOutput';
import { AiInsightCard } from './ui/AiInsightCard';
import { useConfirm } from './ui/ConfirmDialog';
import {
  useApInvoice,
  openApInvoiceFile,
  useApproveApInvoice,
  useUpdateApInvoice,
  useApInvoiceAction,
  useApplySuggestion,
  type ApInvoicePatch,
} from '../hooks/useApInvoices';
import { useUndoAiAction } from '../hooks/useAiActions';
import { usePurchaseOrders, useMatchApInvoice } from '../hooks/useMatching';
import { useBudgetImpact } from '../hooks/useBudgets';
import { formatAmount, formatDate } from '../lib/format';
import { useCan } from '../hooks/useCan';
import { useToast } from './ui/Toast';

const MATCH_TONE: Record<string, 'neutral' | 'accent' | 'success' | 'warn' | 'muted'> = {
  no_po: 'muted', matched: 'success', partial: 'accent', mismatch: 'warn',
};

interface LineItem {
  description?: string;
  qty?: number;
  unitPriceCents?: number;
  taxRate?: number;
  amountCents?: number;
}

interface EditDraft {
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  currency: string;
  subtotalCents: string;
  taxCents: string;
  totalCents: string;
  notes: string;
}

function buildDraft(inv: {
  invoice_number: string | null;
  issue_date: string | null;
  due_date: string | null;
  currency: string;
  subtotal_cents: string;
  tax_cents: string;
  total_cents: string;
  notes?: string | null;
}): EditDraft {
  return {
    invoiceNumber: inv.invoice_number ?? '',
    issueDate: inv.issue_date ?? '',
    dueDate: inv.due_date ?? '',
    currency: inv.currency,
    subtotalCents: String(inv.subtotal_cents),
    taxCents: String(inv.tax_cents),
    totalCents: String(inv.total_cents),
    notes: inv.notes ?? '',
  };
}

export function InboxInvoiceDrawer({
  wsId,
  invoiceId,
  open,
  onOpenChange,
  editable = false,
}: {
  wsId: string;
  invoiceId: string | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Show an Edit button that flips the fields into inputs (saves via PATCH). */
  editable?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { canMutate } = useCan();
  const confirm = useConfirm();
  const { data: inv, isLoading } = useApInvoice(wsId, invoiceId);
  const approve = useApproveApInvoice(wsId);
  const update = useUpdateApInvoice(wsId);
  const action = useApInvoiceAction(wsId);
  const applySuggestion = useApplySuggestion(wsId);
  const undoAiAction = useUndoAiAction(wsId);
  const toast = useToast();
  const pos = usePurchaseOrders(wsId, 'open');
  const runMatch = useMatchApInvoice(wsId);
  const [selectedPo, setSelectedPo] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reset edit state whenever the drawer opens on a new invoice.
  useEffect(() => {
    setEditing(false);
    setSaveError(null);
    setDraft(inv ? buildDraft(inv) : null);
    setSelectedPo(inv?.po_id ?? '');
  }, [inv?.id, open]);

  const extracted = (inv?.extracted ?? {}) as Record<string, unknown>;
  const rawItems = Array.isArray((extracted as { lineItems?: unknown }).lineItems)
    ? ((extracted as { lineItems: unknown[] }).lineItems as LineItem[])
    : [];
  const vendorName = typeof extracted.vendorName === 'string' ? (extracted.vendorName as string) : null;
  const suggested = inv?.ai_suggested as { accountCode?: string; accountName?: string; reason?: string } | undefined;
  const taxBreakdown = Array.isArray(inv?.tax_breakdown) ? inv!.tax_breakdown : [];
  // Apply the AI suggestion through the receipted endpoint so every apply is
  // logged as an undoable ai_action. On success, show a receipt toast with a
  // working inline Undo that reverses the action server-side.
  const applyAccount = () => {
    if (!inv) return;
    applySuggestion.mutate(inv.id, {
      onSuccess: ({ action: rec }) => {
        const accountName =
          (rec.after as { accountName?: string | null })?.accountName ??
          suggested?.accountName ??
          suggested?.accountCode ??
          '';
        toast.withAction(
          'success',
          t('aiActions.applied', { agent: 'Account-Suggest', name: accountName }),
          {
            label: t('aiActions.undo'),
            onClick: () =>
              undoAiAction.mutate(rec.id, {
                onError: () => toast.error(t('aiActions.undoFailed')),
              }),
          },
        );
      },
      onError: (err: any) =>
        toast.error(err?.response?.data?.message ?? t('aiActions.undoFailed')),
    });
  };

  // Pre-approval budget impact: use the assigned expense account (or the AI
  // suggestion as a fallback) and the invoice issue/due date. Non-blocking —
  // shows nothing when no budget covers the period.
  const impactAccountCode = inv?.expense_account_code ?? suggested?.accountCode ?? null;
  const impactDate = inv?.issue_date ?? inv?.due_date ?? null;
  const impactAmount = inv ? Number(inv.total_cents) : null;
  const impact = useBudgetImpact(wsId, {
    glAccountCode: impactAccountCode,
    amountCents: impactAmount,
    onDate: impactDate,
  });
  const impactData = impact.data;

  const onSave = async () => {
    if (!inv || !draft) return;
    setSaveError(null);
    // Build a patch with only changed fields. Empty-string for dates
    // collapses to null (clearing the date); everything else is sent
    // as a plain string/number.
    const patch: ApInvoicePatch = {};
    if (draft.invoiceNumber !== (inv.invoice_number ?? '')) {
      patch.invoiceNumber = draft.invoiceNumber || null;
    }
    if (draft.issueDate !== (inv.issue_date ?? '')) {
      patch.issueDate = draft.issueDate || null;
    }
    if (draft.dueDate !== (inv.due_date ?? '')) {
      patch.dueDate = draft.dueDate || null;
    }
    if (draft.currency !== inv.currency) patch.currency = draft.currency;
    const sub = Number(draft.subtotalCents);
    const tax = Number(draft.taxCents);
    const tot = Number(draft.totalCents);
    if (Number.isFinite(sub) && sub !== Number(inv.subtotal_cents)) patch.subtotalCents = sub;
    if (Number.isFinite(tax) && tax !== Number(inv.tax_cents)) patch.taxCents = tax;
    if (Number.isFinite(tot) && tot !== Number(inv.total_cents)) patch.totalCents = tot;
    if (draft.notes !== (inv.notes ?? '')) patch.notes = draft.notes || null;
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    try {
      await update.mutateAsync({ id: inv.id, patch });
      setEditing(false);
    } catch (err: any) {
      setSaveError(err?.response?.data?.message ?? err?.message ?? 'Save failed');
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={inv?.invoice_number ?? t('inbox.noInvoiceNumber')}
      description={vendorName ?? undefined}
      footer={
        inv ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => openApInvoiceFile(wsId, inv.id)}
              className="inline-flex items-center gap-1.5 text-sm text-ink-600 hover:text-ink-900 dark:text-ink-300 dark:hover:text-ink-100"
            >
              <ExternalLink size={14} />
              {t('inbox.viewOriginal')}
            </button>
            <div className="flex items-center gap-2">
              {editable && canMutate && !editing && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
                >
                  <Pencil size={12} />
                  {t('common.edit')}
                </button>
              )}
              {editing && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setDraft(inv ? buildDraft(inv) : null);
                      setSaveError(null);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
                  >
                    <X size={12} />
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={onSave}
                    disabled={update.isPending}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
                  >
                    <Save size={12} />
                    {t('common.save')}
                  </button>
                </>
              )}
              {canMutate && !editing && inv.status !== 'paid' && inv.status !== 'void' && (
                <>
                  <button
                    type="button"
                    onClick={() => action.mutate({ id: inv.id, action: 'dispute' })}
                    disabled={action.isPending}
                    className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900 disabled:opacity-50"
                  >
                    {t('inbox.dispute')}
                  </button>
                  <button
                    type="button"
                    onClick={async () => { if (await confirm({ title: t('inbox.confirmVoid'), confirmLabel: t('inbox.void'), destructive: true })) action.mutate({ id: inv.id, action: 'void' }); }}
                    disabled={action.isPending}
                    className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-warn-50 dark:hover:bg-warn-900/20 text-warn-600 disabled:opacity-50"
                  >
                    {t('inbox.void')}
                  </button>
                </>
              )}
              {canMutate && !editing && inv.status === 'pending_review' && (
                <button
                  type="button"
                  onClick={() => approve.mutate(inv.id)}
                  disabled={approve.isPending}
                  className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
                >
                  {t('common.approve')}
                </button>
              )}
            </div>
          </div>
        ) : null
      }
    >
      {isLoading || !inv ? (
        <div className="flex items-center gap-2 text-sm text-ink-500 py-10 justify-center">
          <Loader2 size={14} className="animate-spin" />
          {t('common.loading')}
        </div>
      ) : (
        <div className="space-y-6">
          {saveError && (
            <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
              {saveError}
            </div>
          )}

          {inv.posting_review_reason && (
            <div className="rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Pill tone="warn">{t('invoicesAp.needsReview')}</Pill>
              </div>
              <div className="text-xs text-ink-600 dark:text-ink-300">
                <span className="text-ink-500">{t('invoicesAp.reviewReasonLabel')}: </span>
                {inv.posting_review_reason}
              </div>
            </div>
          )}

          <section className="grid grid-cols-2 gap-x-4 gap-y-4 text-sm">
            <Field label={t('inbox.field.vendor')} value={vendorName ?? '—'} />
            <Field
              label={t('inbox.field.registration')}
              value={
                inv.jp_invoice_registration ? (
                  <Pill tone={inv.jp_qualified ? 'success' : 'muted'}>
                    {inv.jp_invoice_registration}
                  </Pill>
                ) : (
                  '—'
                )
              }
            />

            {editing && draft ? (
              <>
                <EditField
                  label={t('inbox.field.invoiceNumber')}
                  value={draft.invoiceNumber}
                  onChange={(v) => setDraft({ ...draft, invoiceNumber: v })}
                />
                <EditField
                  label={t('inbox.field.currency')}
                  value={draft.currency}
                  onChange={(v) => setDraft({ ...draft, currency: v.toUpperCase().slice(0, 3) })}
                />
                <EditField
                  label={t('inbox.field.issueDate')}
                  type="date"
                  value={draft.issueDate}
                  onChange={(v) => setDraft({ ...draft, issueDate: v })}
                />
                <EditField
                  label={t('inbox.field.dueDate')}
                  type="date"
                  value={draft.dueDate}
                  onChange={(v) => setDraft({ ...draft, dueDate: v })}
                />
                <EditField
                  label={t('inbox.field.subtotal')}
                  type="number"
                  value={draft.subtotalCents}
                  onChange={(v) => setDraft({ ...draft, subtotalCents: v })}
                  hint={t('inbox.cents')}
                />
                <EditField
                  label={t('inbox.field.tax')}
                  type="number"
                  value={draft.taxCents}
                  onChange={(v) => setDraft({ ...draft, taxCents: v })}
                  hint={t('inbox.cents')}
                />
                <EditField
                  label={t('inbox.field.total')}
                  type="number"
                  value={draft.totalCents}
                  onChange={(v) => setDraft({ ...draft, totalCents: v })}
                  hint={t('inbox.cents')}
                />
                <div className="col-span-2">
                  <EditField
                    label={t('inbox.field.notes')}
                    value={draft.notes}
                    onChange={(v) => setDraft({ ...draft, notes: v })}
                    textarea
                  />
                </div>
              </>
            ) : (
              <>
                <Field label={t('inbox.field.invoiceNumber')} value={inv.invoice_number ?? '—'} />
                <Field label={t('inbox.field.currency')} value={inv.currency} />
                <Field
                  label={t('inbox.field.issueDate')}
                  value={formatDate(inv.issue_date, i18n.language)}
                />
                <Field label={t('inbox.field.dueDate')} value={formatDate(inv.due_date, i18n.language)} />
                <Field
                  label={t('inbox.field.subtotal')}
                  value={formatAmount(inv.subtotal_cents, inv.currency, i18n.language)}
                />
                <Field
                  label={t('inbox.field.tax')}
                  value={formatAmount(inv.tax_cents, inv.currency, i18n.language)}
                />
                <Field
                  label={t('inbox.field.total')}
                  value={
                    <span className="num text-base font-semibold">
                      {formatAmount(inv.total_cents, inv.currency, i18n.language)}
                    </span>
                  }
                />
                <Field
                  label={t('inbox.field.ocrConfidence')}
                  value={
                    inv.ocr_confidence
                      ? `${(Number(inv.ocr_confidence) * 100).toFixed(0)}%`
                      : '—'
                  }
                />
                {inv.notes && (
                  <div className="col-span-2">
                    <Field label={t('inbox.field.notes')} value={inv.notes} />
                  </div>
                )}
              </>
            )}
          </section>

          <section>
            <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">{t('inbox.glAccount')}</h3>
            <div className="rounded-lg border border-ink-200 dark:border-ink-800 p-3 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-ink-500">{t('inbox.expenseAccount')}</span>
                <span className="num font-medium">{inv.expense_account_code ?? t('inbox.accountDefault')}</span>
              </div>
              {suggested?.accountCode && suggested.accountCode !== inv.expense_account_code && (
                <div className="flex items-center justify-between gap-2 rounded-md bg-accent-50 dark:bg-accent-900/20 px-2 py-1.5">
                  <span className="text-xs text-ink-600 dark:text-ink-300">
                    {t('inbox.aiSuggests')}: <span className="num font-medium">{suggested.accountCode}</span>
                    {suggested.accountName ? ` · ${suggested.accountName}` : ''}
                  </span>
                  {canMutate && (
                    <button
                      type="button"
                      onClick={applyAccount}
                      disabled={applySuggestion.isPending}
                      className="px-2 py-1 rounded-md bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
                    >
                      {t('inbox.apply')}
                    </button>
                  )}
                </div>
              )}
              {suggested?.reason && (
                <div className="rounded-md bg-ink-50 dark:bg-ink-900/50 px-2.5 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-ink-400 mb-1">
                    {t('inbox.aiReasoning')}
                  </div>
                  <AiOutput
                    copyable={false}
                    citations={[
                      ...(vendorName && inv.vendor_id
                        ? [{ kind: 'vendor', id: inv.vendor_id, label: vendorName }]
                        : []),
                      { kind: 'ap_invoice', id: inv.id, label: inv.invoice_number ?? t('inbox.noInvoiceNumber') },
                    ]}
                  >
                    {suggested.reason}
                  </AiOutput>
                </div>
              )}
              {impactData && (
                <div
                  className={
                    'flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs ' +
                    (impactData.overBudget
                      ? 'bg-warn-50 dark:bg-warn-900/20 text-warn-600'
                      : (impactData.projectedUtilizationPct ?? 0) > 80
                        ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-200'
                        : 'bg-ink-50 dark:bg-ink-900/50 text-ink-600 dark:text-ink-300')
                  }
                >
                  <span>
                    <span className="text-ink-500">{t('budgets.impactLabel')}: </span>
                    {impactData.currentUtilizationPct ?? 0}% {t('budgets.afterThis', {
                      pct: impactData.projectedUtilizationPct ?? 0,
                    })}
                  </span>
                  {impactData.overBudget && <Pill tone="warn">{t('budgets.overBudget')}</Pill>}
                </div>
              )}
            </div>
          </section>

          {taxBreakdown.length > 0 && (
            <section>
              <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
                {t('invoicesAp.taxBreakdown')}
              </h3>
              <div className="rounded-lg border border-ink-200 dark:border-ink-800 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-ink-50 dark:bg-ink-900/50 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('invoicesAp.taxRate')}</th>
                      <th className="px-3 py-2 font-medium text-right">{t('invoicesAp.taxable')}</th>
                      <th className="px-3 py-2 font-medium text-right">{t('invoicesAp.taxAmount')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-200 dark:divide-ink-800">
                    {taxBreakdown.map((b, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2 num">{b.rate}%</td>
                        <td className="px-3 py-2 text-right num">
                          {formatAmount(b.taxableCents, inv.currency, i18n.language)}
                        </td>
                        <td className="px-3 py-2 text-right num">
                          {formatAmount(b.taxCents, inv.currency, i18n.language)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs uppercase tracking-wide text-ink-500">
                {t('matching.title')}
              </h3>
              {inv.po_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/app/workspace/${wsId}/matching?po=${inv.po_id}`)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-600 hover:text-accent-700"
                >
                  <ExternalLink size={12} />
                  {t('inbox.viewInMatching')}
                </button>
              )}
            </div>
            <div className="rounded-lg border border-ink-200 dark:border-ink-800 p-3 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-ink-500">{t('invoicesAp.matchStatus')}:</span>
                <Pill tone={MATCH_TONE[inv.match_status] ?? 'muted'}>
                  {t(`invoicesAp.matchStatuses.${inv.match_status}`)}
                </Pill>
              </div>
              {inv.match_detail && typeof (inv.match_detail as Record<string, unknown>).reason === 'string' && (
                <p className="text-xs text-ink-500">{String((inv.match_detail as Record<string, unknown>).reason)}</p>
              )}
              {canMutate && (
                <div className="flex items-center gap-2">
                  <select
                    value={selectedPo}
                    onChange={(e) => setSelectedPo(e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
                  >
                    <option value="">{t('matching.linkPo')}</option>
                    {(pos.data ?? []).map((po) => (
                      <option key={po.id} value={po.id}>{po.po_number}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => runMatch.mutate({ apId: inv.id, poId: selectedPo || null })}
                    disabled={runMatch.isPending}
                    className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
                  >
                    {t('common.run')}
                  </button>
                </div>
              )}
            </div>
          </section>

          {inv.status === 'disputed' && (
            <section>
              <AiInsightCard
                agent="dispute_composer"
                title={t('aiOutput.cards.disputeDraft')}
                prompt={t('aiOutput.prompts.disputeDraft', {
                  number: inv.invoice_number ?? inv.id.slice(0, 8),
                })}
                targetKind="ap_invoice"
                targetId={inv.id}
                tone="recommendation"
              />
            </section>
          )}

          {rawItems.length > 0 && (
            <section>
              <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
                {t('inbox.lineItems')}
              </h3>
              <div className="rounded-lg border border-ink-200 dark:border-ink-800 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-ink-50 dark:bg-ink-900/50 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('inbox.col.description')}</th>
                      <th className="px-3 py-2 font-medium text-right">{t('inbox.col.qty')}</th>
                      <th className="px-3 py-2 font-medium text-right">{t('inbox.col.unit')}</th>
                      <th className="px-3 py-2 font-medium text-right">{t('inbox.col.amount')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-200 dark:divide-ink-800">
                    {rawItems.map((li, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{li.description ?? '—'}</td>
                        <td className="px-3 py-2 text-right num">{li.qty ?? '—'}</td>
                        <td className="px-3 py-2 text-right num">
                          {li.unitPriceCents != null
                            ? formatAmount(li.unitPriceCents, inv.currency, i18n.language)
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-right num">
                          {li.amountCents != null
                            ? formatAmount(li.amountCents, inv.currency, i18n.language)
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {inv.raw_ocr_text && (
            <section>
              <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2 flex items-center gap-1.5">
                <FileText size={12} />
                {t('inbox.rawText')}
              </h3>
              <pre className="text-[11px] leading-relaxed p-3 rounded-lg bg-ink-50 dark:bg-ink-900/50 border border-ink-200 dark:border-ink-800 max-h-80 overflow-auto whitespace-pre-wrap break-words">
                {inv.raw_ocr_text.slice(0, 6000)}
                {inv.raw_ocr_text.length > 6000 ? `\n\n…(${inv.raw_ocr_text.length - 6000} more chars)` : ''}
              </pre>
            </section>
          )}
        </div>
      )}
    </Drawer>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-ink-500">{label}</div>
      <div className="mt-0.5 truncate">{value}</div>
    </div>
  );
}

function EditField({
  label,
  value,
  onChange,
  type = 'text',
  textarea,
  hint,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  type?: 'text' | 'number' | 'date';
  textarea?: boolean;
  hint?: string;
}) {
  const className =
    'mt-0.5 w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
    'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';
  return (
    <div className="min-w-0">
      <label className="text-[11px] uppercase tracking-wide text-ink-500">
        {label}
        {hint && <span className="ml-1 normal-case tracking-normal text-ink-400">({hint})</span>}
      </label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className={className}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={className}
        />
      )}
    </div>
  );
}
